// @spec IAM-STR-006
// @spec IAM-STR-011
// @spec IAM-STR-036
// @spec SHL-STR-005
'use client';

import { UserProfile } from '@clerk/clerk-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DeleteAccountSection, MfaStatusCard, PasswordChangeForm } from '@/features/security';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/security/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.security'),
  }),
  component: SecuritySettingsPage,
});

function SecuritySettingsPage() {
  const { t } = useTranslation('settings');
  const mfaSectionRef = useRef<HTMLDivElement>(null);

  const handleManageMfa = () => {
    mfaSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('security.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('security.manageSettings')}</p>
        </header>

        <MfaStatusCard onManageMfa={handleManageMfa} />

        <PasswordChangeForm />

        <section className="rounded-lg border p-4">
          <h2 className="text-base font-semibold text-foreground">{t('security.activeSessions')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('security.activeSessionsDescription')}
          </p>
          <Link to="/settings/security/sessions" className="mt-3 inline-block">
            <Button variant="outline">{t('security.manageSessions')}</Button>
          </Link>
        </section>

        <DeleteAccountSection />

        {/* Clerk UserProfile for MFA enrollment and management */}
        <section ref={mfaSectionRef} data-testid="mfa-management-section">
          <h2 className="mb-4 text-lg font-semibold text-foreground">
            {t('security.mfaManagement')}
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('security.mfaManagementDescription')}
          </p>
          <div className="overflow-hidden rounded-lg border">
            <UserProfile
              routing="virtual"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'shadow-none border-0',
                  navbar: 'hidden',
                  navbarMobileMenuButton: 'hidden',
                  pageScrollBox: 'p-0',
                },
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
